import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Extension } from '@tiptap/core';
import { Button, ButtonGroup, Form } from 'react-bootstrap';
import './PrEditor.css';

// TextStyle-based font-size extension (spec: small / normal / large sizes).
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const SIZE_PLACEHOLDER = '__size__';

const SIZE_OPTIONS = [
  { label: 'Small', value: '0.85em' },
  { label: 'Normal', value: 'normal' },
  { label: 'Large', value: '1.25em' },
  { label: 'Heading', value: 'heading' },
];

function PrEditor({ onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      FontSize,
      Underline,
      Link.configure({ openOnClick: false, defaultProtocol: 'https' }),
    ],
    content: '',
    // TipTap v3 defaults this to false; keep toolbar active states in sync.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => onChange(e.getHTML(), e.isEmpty),
  });

  if (!editor) return null;

  const applySize = (value) => {
    if (value === 'heading') {
      editor.chain().focus().setHeading({ level: 3 }).run();
    } else if (value === 'normal') {
      editor.chain().focus().setParagraph().unsetFontSize().run();
    } else {
      editor.chain().focus().setParagraph().setFontSize(value).run();
    }
  };

  const applyLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL', previousUrl);
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="pr-editor border rounded">
      <div className="d-flex flex-wrap gap-2 align-items-center border-bottom p-2">
        <ButtonGroup size="sm">
          <Button
            variant={editor.isActive('bold') ? 'dark' : 'outline-dark'}
            onClick={() => editor.chain().focus().toggleBold().run()}
            aria-label="Bold"
          >
            <strong>B</strong>
          </Button>
          <Button
            variant={editor.isActive('italic') ? 'dark' : 'outline-dark'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            aria-label="Italic"
          >
            <em>I</em>
          </Button>
          <Button
            variant={editor.isActive('underline') ? 'dark' : 'outline-dark'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-label="Underline"
          >
            <u>U</u>
          </Button>
          <Button
            variant={editor.isActive('link') ? 'dark' : 'outline-dark'}
            onClick={applyLink}
            aria-label="Link"
          >
            Link
          </Button>
        </ButtonGroup>
        <Form.Select
          size="sm"
          style={{ width: 'auto' }}
          aria-label="Text size"
          value={SIZE_PLACEHOLDER}
          onChange={(e) => applySize(e.target.value)}
        >
          <option value={SIZE_PLACEHOLDER} disabled>
            Size
          </option>
          {SIZE_OPTIONS.map((o) => (
            <option key={o.label} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          type="color"
          size="sm"
          style={{ width: '2.5rem' }}
          aria-label="Text color"
          defaultValue="#000000"
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />
      </div>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
}

export default PrEditor;
